// ──────────────────────────────────────────────────────────────────────────
// NetScope Console — design tokens
//
// The one place a colour or a size is written. Two consumers read it:
//   - main.tsx injects cssVariables() so theme.css and inline var(--…)
//     styles resolve them;
//   - theme.ts builds both Ant Design themes from the same palettes.
//
// scripts/check-tokens.mjs imports this file straight into Node, so keep it
// to erasable TypeScript: no enum, no namespace, no parameter properties.
// ──────────────────────────────────────────────────────────────────────────

export type ThemeName = "dark" | "light";

export interface Palette {
  // Surfaces, darkest role first: page, card, elevated, hover.
  bg0: string;
  bg1: string;
  bg2: string;
  bg3: string;
  line: string;
  line2: string;

  // Text, weakest first.
  faint: string; // placeholders, group titles — still AA on bg0..bg2
  muted: string;
  text: string;
  strong: string;

  cyan: string; // primary
  cyan2: string; // primary hover
  teal: string;
  green: string;
  amber: string;
  red: string;
  violet: string;

  onPrimary: string; // text on a cyan fill
  focusRing: string;
  hoverOverlay: string; // base of the translucent menu/row hover wash

  // Chart and widget accents (--series-1…8). A series keeps its index everywhere.
  series: readonly string[];

  headerBgTranslucent: string;
  shadowCard: string;
  shadowCardHover: string;
  shadowHero: string; // the sign-in and license cards
  rowStripeBg: string;
  // Alpha of the status-chip tints: [background, border].
  tintAlpha: readonly [number, number];
  // Red reads heavier than the other hues on a light surface.
  tintAlphaRedBg: number;

  // Alert / banner fills. Ant Design derives these from the status colours,
  // which is fine on dark; from the deep light-theme hues it yields muddy
  // blocks, so the light palette pins them to pastels.
  statusFills?: {
    infoBg: string;
    infoBorder: string;
    successBg: string;
    successBorder: string;
    warningBg: string;
    warningBorder: string;
    errorBg: string;
    errorBorder: string;
  };
}

const dark: Palette = {
  bg0: "#090d18",
  bg1: "#10162a",
  bg2: "#141c34",
  bg3: "#1a2338",
  line: "#212c48",
  line2: "#33406a",

  faint: "#7c8bb0",
  muted: "#94a1c4",
  text: "#e7ecfb",
  strong: "#ffffff",

  cyan: "#38bdf8",
  cyan2: "#7dd3fc",
  teal: "#2dd4bf",
  green: "#34d399",
  amber: "#fbbf24",
  red: "#fb7185",
  violet: "#a78bfa",

  onPrimary: "#001018",
  focusRing: "#38bdf8",
  hoverOverlay: "#ffffff",

  // cyan, violet, emerald, amber, sky, pink, teal, indigo
  series: ["#38bdf8", "#a78bfa", "#34d399", "#fbbf24", "#60a5fa", "#f472b6", "#2dd4bf", "#818cf8"],

  headerBgTranslucent: "rgba(9,13,24,0.72)",
  shadowCard: "0 2px 10px rgba(0,0,0,0.28)",
  shadowCardHover: "0 8px 24px rgba(2,6,18,0.45)",
  shadowHero: "0 28px 90px rgba(0,0,0,0.7)",
  rowStripeBg: "rgba(20,28,52,0.45)",
  tintAlpha: [0.11, 0.22],
  tintAlphaRedBg: 0.11,
};

// Soft slate-blue: surfaces avoid pure white, text avoids pure black, and
// the accents are the deep steps of each hue so they hold AA as text.
const light: Palette = {
  bg0: "#eef2f7",
  bg1: "#f7f9fc",
  bg2: "#eef2f7",
  bg3: "#e4ebf3",
  line: "#d6dee8",
  line2: "#b9c4d2",

  faint: "#606e82",
  muted: "#5d6b7e",
  text: "#2d3748",
  strong: "#1a202c",

  cyan: "#0e7490",
  cyan2: "#0891b2",
  teal: "#0f766e",
  green: "#047857",
  amber: "#b05108",
  red: "#b91c1c",
  violet: "#6d28d9",

  onPrimary: "#ffffff",
  focusRing: "#0e7490",
  hoverOverlay: "#2d3748",

  // The same eight hues, deepened to stay legible as lines on off-white.
  series: ["#0e7490", "#6d28d9", "#047857", "#b05108", "#2563eb", "#be185d", "#0f766e", "#4338ca"],

  headerBgTranslucent: "rgba(238,242,247,0.88)",
  shadowCard: "0 1px 3px rgba(15,23,42,0.06)",
  shadowCardHover: "0 4px 12px rgba(15,23,42,0.08)",
  shadowHero: "0 20px 60px rgba(15,23,42,0.12)",
  rowStripeBg: "rgba(45,55,72,0.025)",
  tintAlpha: [0.08, 0.18],
  tintAlphaRedBg: 0.07,

  statusFills: {
    infoBg: "#e0f2fe",
    infoBorder: "#7dd3fc",
    successBg: "#dcfce7",
    successBorder: "#86efac",
    warningBg: "#fef3c7",
    warningBorder: "#fde68a",
    errorBg: "#fee2e2",
    errorBorder: "#fecaca",
  },
};

export const palettes: Record<ThemeName, Palette> = { dark, light };

// Theme-independent sizes. Nothing in the console is set below 12px.
export const scale = {
  fs: [12, 13, 14, 16, 20, 24],
  sp: [4, 8, 12, 16, 24, 32],
} as const;

type ColorKey = {
  [K in keyof Palette]: Palette[K] extends string ? K : never;
}[keyof Palette];

// Every text-on-surface pair the UI relies on, with its WCAG floor.
// check-tokens.mjs fails the build when a palette edit breaks one.
export const contrastPairs: readonly { fg: ColorKey; bg: ColorKey; min: number }[] = [
  ...(["bg0", "bg1", "bg2"] as const).flatMap(bg =>
    (["text", "muted", "faint"] as const).map(fg => ({ fg, bg, min: 4.5 })),
  ),
  { fg: "text", bg: "bg3", min: 4.5 },
  { fg: "muted", bg: "bg3", min: 4.5 },
  { fg: "faint", bg: "bg3", min: 3 },
  ...(["bg0", "bg1"] as const).flatMap(bg =>
    (["cyan", "green", "amber", "red", "violet", "teal"] as const).map(fg => ({ fg, bg, min: 4.5 })),
  ),
  { fg: "strong", bg: "bg0", min: 7 },
  { fg: "strong", bg: "bg1", min: 7 },
  { fg: "onPrimary", bg: "cyan", min: 4.5 },
];

// tint turns "#rrggbb" into a translucent rgba() of the same hue.
export function tint(hex: string, alpha: number): string {
  const n = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map(i => parseInt(n.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},${alpha})`;
}

function paletteVars(p: Palette): Record<string, string> {
  const [bgA, bdA] = p.tintAlpha;
  const v: Record<string, string> = {
    "--bg-0": p.bg0,
    "--bg-1": p.bg1,
    "--bg-2": p.bg2,
    "--bg-3": p.bg3,
    "--line": p.line,
    "--line-2": p.line2,
    "--faint": p.faint,
    "--muted": p.muted,
    "--text": p.text,
    "--strong": p.strong,
    "--cyan": p.cyan,
    "--cyan-2": p.cyan2,
    "--teal": p.teal,
    "--green": p.green,
    "--amber": p.amber,
    "--red": p.red,
    "--violet": p.violet,
    "--on-primary": p.onPrimary,
    "--focus-ring": p.focusRing,
    "--header-bg-translucent": p.headerBgTranslucent,
    "--shadow-card": p.shadowCard,
    "--shadow-card-hover": p.shadowCardHover,
    "--shadow-hero": p.shadowHero,
    "--row-stripe-bg": p.rowStripeBg,
    "--tint-cyan-bg": tint(p.cyan, bgA),
    "--tint-cyan-bd": tint(p.cyan, bdA),
    "--tint-green-bg": tint(p.green, bgA),
    "--tint-green-bd": tint(p.green, bdA),
    "--tint-amber-bg": tint(p.amber, bgA),
    "--tint-amber-bd": tint(p.amber, bdA),
    "--tint-red-bg": tint(p.red, p.tintAlphaRedBg),
    "--tint-red-bd": tint(p.red, bdA),
  };
  p.series.forEach((c, i) => {
    v[`--series-${i + 1}`] = c;
  });
  return v;
}

function block(selector: string, vars: Record<string, string>): string {
  const body = Object.entries(vars)
    .map(([k, val]) => `  ${k}: ${val};`)
    .join("\n");
  return `${selector} {\n${body}\n}`;
}

// cssVariables renders the palettes as the :root blocks theme.css used to
// declare by hand. Dark is the default; [data-theme="light"] overrides it.
export function cssVariables(): string {
  const sizes: Record<string, string> = {};
  scale.fs.forEach(n => {
    sizes[`--fs-${n}`] = `${n}px`;
  });
  scale.sp.forEach(n => {
    sizes[`--sp-${n}`] = `${n}px`;
  });
  return [
    block(":root", { ...paletteVars(palettes.dark), ...sizes }),
    block(':root[data-theme="light"]', paletteVars(palettes.light)),
  ].join("\n\n");
}
